// ── cohort-form-modal: the create/edit cohort Modal (POST/PATCH cohorts) ─────
// Wave 3 (Cohorts). One @vjal/ui Modal backing both create (POST /cohorts) and
// edit (PATCH /cohorts/:id) — `mode` only changes the title, submit label, and
// initial values. Presentational + validation-only; the server is authoritative
// (the live route re-checks the role/scope and 403s, which we render honestly
// inline via describeCohortWriteError).
//
// FR-COHORT-001 (the historical "locked dropdown" bug — fixed here):
//  - **Company** is an AsyncSelect sourced from companies.list (loading/empty/
//    error-aware), EDITABLE for authorized users, persisted via cohorts.create/
//    update. A non-authorized role never opens this modal (the page gates the
//    affordance), but as a belt-and-suspenders the `readOnly` prop disables every
//    field so the dropdowns can never be edited without the write capability.
//  - **Status** is a Select of the valid cohort statuses (COHORT_WRITE_STATUSES),
//    EDITABLE on edit (create defaults to draft). The server validates the value.
//    Completed is not offered: completion runs through the Complete cohort
//    confirmation. An already-completed cohort shows Completed as its current
//    status, so saving other fields never reopens it.
//    Moving a cohort to Cancelled is destructive, so it uses the armed confirm
//    (WEB-037): the first Save arms (consequence text + "Confirm cancellation"),
//    the second submits, and changing any field or closing disarms.
//
// Cohort-side PROGRAM ASSIGNMENT (the Wave-2 deferral, completed here):
//  - the cohort carries a SINGLE program_id; this **Program** Select (sourced
//    from programs.list, ACTIVE programs only — archived programs are NOT
//    selectable for new assignment) IS the program->cohort assignment. There is
//    no separate assign endpoint; setting program_id on create/update is the
//    assignment (blueprint §1 + the Wave-2 program-cohorts-section note).
//
// Honest write contract: a server rejection (validation / permission-denied /
// gate-closed / unavailable) is surfaced INLINE — never swallowed, never faked.
// The owning page runs the mutation and closes + toasts ONLY after the backend
// confirms success.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Alert, AsyncSelect, Button, Input, Modal, Select, humanizeLabel } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type {
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingProgramDto,
  CoachingUserDto,
  ListResponse
} from "../../services/coaching-dtos";
import { COHORT_WRITE_STATUSES, describeCohortWriteError, type CohortFormStatus } from "./cohorts-write";

const services = getCoachingServices();

/** Roles that can own a cohort as its coach (UI hint only; server re-checks). */
const COACH_ROLE = "coach";
/** Programs in these statuses are NOT selectable for a NEW assignment. */
const ARCHIVED_PROGRAM_STATUS = "archived";

export interface CohortFormValues {
  name: string;
  companyId: string;
  programId: string;
  coachId: string;
  status: CohortFormStatus;
}

export interface CohortFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  /** The cohort being edited (edit mode only) — seeds the initial values. */
  cohort?: CoachingCohortDto | null;
  /** Read-only roles never write; belt-and-suspenders disables every field. */
  readOnly?: boolean;
  onClose: () => void;
  /** The actual write — owned by the page (react-query mutation). */
  onSubmit: (values: CohortFormValues) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

interface FieldErrors {
  name?: string;
  companyId?: string;
  programId?: string;
  coachId?: string;
}

function initialValues(mode: "create" | "edit", cohort?: CoachingCohortDto | null): CohortFormValues {
  if (mode === "edit" && cohort) {
    // An already-completed cohort keeps Completed, so saving other fields never reopens it.
    const current = cohort.status ?? "";
    const status: CohortFormStatus =
      current === "completed" || (COHORT_WRITE_STATUSES as readonly string[]).includes(current)
        ? (current as CohortFormStatus)
        : "active";
    return {
      name: cohort.name ?? "",
      companyId: cohort.company_id ?? "",
      programId: cohort.program_id ?? "",
      coachId: cohort.coach_id ?? "",
      status
    };
  }
  return { name: "", companyId: "", programId: "", coachId: "", status: "draft" };
}

export function CohortFormModal({
  open,
  mode,
  cohort,
  readOnly = false,
  onClose,
  onSubmit,
  submitting = false,
  submitError
}: CohortFormModalProps) {
  const [values, setValues] = useState<CohortFormValues>(() => initialValues(mode, cohort));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [cancelArmed, setCancelArmed] = useState(false);
  const formId = useId();
  const cancellingCohort = mode === "edit" && values.status === "cancelled" && cohort?.status !== "cancelled";
  const statusOptions: readonly CohortFormStatus[] =
    cohort?.status === "completed" ? ["completed", ...COHORT_WRITE_STATUSES] : COHORT_WRITE_STATUSES;
  const close = () => {
    setCancelArmed(false);
    onClose();
  };

  // Option reads (only while the modal is open). Honest: an unavailable read
  // surfaces a loadError on the AsyncSelect / disables the Select with a neutral
  // hint, never a fabricated option.
  const companyQuery = useHonestQuery<ListResponse<CoachingCompanyDto>>(
    coachingQueryKeys.list("companies.list"),
    () => services.companies.list(),
    { enabled: open }
  );
  const programQuery = useHonestQuery<ListResponse<CoachingProgramDto>>(
    coachingQueryKeys.list("programs.list"),
    () => services.programs.list(),
    { enabled: open }
  );
  const userQuery = useHonestQuery<ListResponse<CoachingUserDto>>(
    coachingQueryKeys.list("users.list"),
    () => services.users.list(),
    { enabled: open }
  );

  const companyOptions = useMemo(
    () =>
      (companyQuery.data?.items ?? []).map((company) => ({
        value: company.id,
        label: company.name || "Unnamed company"
      })),
    [companyQuery.data]
  );

  // Program assignment: ACTIVE programs only for new assignment; archived
  // programs are not selectable. An already-assigned archived program is kept
  // visible (disabled) so the edit form doesn't silently drop it.
  // COMPANY-SCOPED: the cohort's program must belong to the cohort's company
  // (the API rejects an (org, company, program) mismatch), so once a company is
  // selected only its programs (or company-less programs) are offered.
  const programOptions = useMemo(() => {
    const all = programQuery.data?.items ?? [];
    return all
      .filter((program) => program.status !== ARCHIVED_PROGRAM_STATUS || String(program.id) === String(values.programId))
      .filter(
        (program) =>
          !values.companyId ||
          // Company-less programs are org-wide and remain assignable.
          !program.company_id ||
          String(program.company_id) === String(values.companyId) ||
          String(program.id) === String(values.programId)
      )
      .map((program) => ({
        id: program.id,
        label: program.name || "Unnamed program",
        archived: program.status === ARCHIVED_PROGRAM_STATUS
      }));
  }, [programQuery.data, values.programId, values.companyId]);

  const coachOptions = useMemo(
    () => (userQuery.data?.items ?? []).filter((user) => (user.role ?? "") === COACH_ROLE),
    [userQuery.data]
  );

  // Seed ONLY on the open false->true transition (and when the target cohort
  // identity changes), never on every parent re-render — otherwise an in-progress
  // edit would be clobbered by a background re-render.
  const wasOpen = useRef(false);
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const cohortKey = cohort?.id ?? null;
    if (open && (!wasOpen.current || seededFor.current !== cohortKey)) {
      setValues(initialValues(mode, cohort));
      setErrors({});
      setCancelArmed(false);
      seededFor.current = cohortKey;
    }
    wasOpen.current = open;
  }, [open, mode, cohort]);

  const set = <K extends keyof CohortFormValues>(key: K, value: CohortFormValues[K]) => {
    setCancelArmed(false);
    setValues((current) => {
      const next = { ...current, [key]: value };
      // Changing company invalidates a program that belongs to another company.
      if (key === "companyId" && current.programId) {
        const stillValid = (programQuery.data?.items ?? []).some(
          (program) => String(program.id) === String(current.programId) && (!program.company_id || String(program.company_id) === String(value))
        );
        if (!stillValid) next.programId = "";
      }
      return next;
    });
  };

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!values.name.trim()) next.name = "Cohort name is required.";
    if (!values.companyId) next.companyId = "Select a company for this cohort.";
    if (mode === "create") {
      if (!values.programId) next.programId = "Select a program for this cohort.";
      if (!values.coachId) next.coachId = "Select an assigned coach for this cohort.";
    }
    return next;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (readOnly) return;
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    setErrors({});
    // WEB-037: the first Save on a cancellation only arms; the second submits.
    if (cancellingCohort && !cancelArmed) {
      setCancelArmed(true);
      return;
    }
    try {
      await onSubmit({
        name: values.name.trim(),
        companyId: values.companyId,
        programId: values.programId,
        coachId: values.coachId,
        status: values.status
      });
      // The page closes the modal + toasts on success; nothing else to do here.
    } catch {
      // The owning mutation surfaces the error via `submitError`; swallow the
      // re-throw so the modal stays open with the inline error shown.
    } finally {
      setCancelArmed(false);
    }
  }

  const topError = describeCohortWriteError(submitError);
  const programDisabled = readOnly || (programQuery.posture !== null && programQuery.posture !== "empty");
  const coachDisabled = readOnly || (userQuery.posture !== null && userQuery.posture !== "empty");

  return (
    <Modal open={open} onClose={close} title={mode === "create" ? "Add cohort" : "Edit cohort"}>
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4" noValidate>
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}
        {readOnly && (
          <Alert variant="info">Your role can view this cohort's configuration but can't change it.</Alert>
        )}

        <Input
          label="Cohort name"
          value={values.name}
          onChange={(event) => set("name", event.target.value)}
          error={errors.name}
          disabled={readOnly}
          required
        />

        {/* FR-COHORT-001: Company is an EDITABLE AsyncSelect for authorized users. */}
        <AsyncSelect
          label="Company"
          value={values.companyId}
          options={companyOptions}
          placeholder="Select a company…"
          loading={companyQuery.posture === "loading"}
          loadError={
            companyQuery.posture !== null && companyQuery.posture !== "empty" && companyQuery.posture !== "loading"
              ? "Companies are unavailable right now. Retry to load them."
              : undefined
          }
          onRetry={companyQuery.retry}
          emptyLabel="No companies available — create a company first."
          error={errors.companyId}
          disabled={readOnly}
          onChange={(event) => set("companyId", event.target.value)}
        />

        {/* Cohort-side PROGRAM ASSIGNMENT: setting program_id IS the assignment. */}
        <Select
          label="Program"
          value={values.programId}
          disabled={programDisabled}
          className="disabled:!text-ink-muted"
          hint={
            programDisabled && !readOnly
              ? "Programs are unavailable right now; you can set this later."
              : "Assigning a program here is the program → cohort assignment (active programs only)."
          }
          error={errors.programId}
          onChange={(event) => set("programId", event.target.value)}
        >
          <option value="">No program</option>
          {programOptions.map((program) => (
            <option key={program.id} value={program.id} disabled={program.archived}>
              {program.label}
              {program.archived ? " (archived)" : ""}
            </option>
          ))}
        </Select>

        <Select
          label="Assigned coach"
          value={values.coachId}
          disabled={coachDisabled}
          className="disabled:!text-ink-muted"
          hint={coachDisabled && !readOnly ? "Coaches are unavailable right now; you can set this later." : undefined}
          error={errors.coachId}
          onChange={(event) => set("coachId", event.target.value)}
        >
          <option value="">No coach</option>
          {coachOptions.map((coach) => (
            <option key={coach.id} value={coach.id}>
              {coach.name || coach.email || "Unnamed coach"}
            </option>
          ))}
        </Select>

        {/* FR-COHORT-001: Status is an EDITABLE Select on edit (create = draft). */}
        {mode === "edit" && (
          <Select
            label="Status"
            value={values.status}
            disabled={readOnly}
            onChange={(event) => set("status", event.target.value as CohortFormStatus)}
            hint="Completion runs through the Complete cohort confirmation, which validates all sessions are done."
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {humanizeLabel(status)}
              </option>
            ))}
          </Select>
        )}

        {cancelArmed && (
          <Alert variant="warning">
            Cancel this cohort? It becomes Cancelled and inactive. Its sessions, participants and reports stay as they
            are. Select Confirm cancellation to continue, or change any field to go back.
          </Alert>
        )}

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          {!readOnly && (
            <Button
              type="submit"
              variant={cancelArmed ? "danger" : "primary"}
              className={cancelArmed ? destructiveButtonClass : undefined}
              isLoading={submitting}
            >
              {mode === "create" ? "Create cohort" : cancelArmed ? "Confirm cancellation" : "Save changes"}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}
